// Thin fetch wrapper: same-origin cookies, JSON, German error messages from the server
export const BASE = import.meta.env.BASE_URL.replace(/\/$/, ''); // "/app"

export class ApiError extends Error {
  constructor(
    public status: number,
    message: string,
  ) {
    super(message);
  }
}

export async function api<T = unknown>(path: string, init: { method?: string; body?: unknown } = {}): Promise<T> {
  const res = await fetch(`${BASE}/api${path}`, {
    method: init.method || 'GET',
    credentials: 'same-origin',
    headers: {
      'X-Requested-With': 'sportplaner',
      ...(init.body !== undefined ? { 'Content-Type': 'application/json' } : {}),
    },
    body: init.body !== undefined ? JSON.stringify(init.body) : undefined,
  });
  const data: unknown = res.headers.get('content-type')?.includes('application/json') ? await res.json() : null;
  if (!res.ok) throw new ApiError(res.status, errorText(data) || 'Das hat nicht geklappt. Bitte erneut versuchen.');
  return data as T;
}

function errorText(data: unknown): string {
  return data && typeof data === 'object' && 'error' in data && typeof data.error === 'string' ? data.error : '';
}

export const mediaUrl = (p: string | null | undefined) => (p ? `${BASE}/${p.replace(/^\/+/, '')}` : null);

// ---- API types ----
export interface Me {
  id: number;
  first_name: string;
  last_name: string;
  email: string | null;
  photo: string | null;
  role: 'member' | 'admin';
  /** own sign-ups per sport, last 12 months (only from /auth/me) */
  sport_counts?: Record<string, number>;
}
export interface MeDetails extends Me {
  phone: string | null;
  street: string | null;
  house_number: string | null;
  zip: string | null;
  city: string | null;
  notify_events: boolean;
  notify_market: boolean;
  notify_posts: boolean;
  notify_self: boolean;
  push_events: boolean;
  push_market: boolean;
  push_posts: boolean;
}
export interface PersonRef {
  id: number | null;
  name: string;
  photo: string | null;
  former?: boolean;
}
export type MyStatus = 'yes' | 'maybe' | 'wait' | 'none';
export interface EventBase {
  id: number;
  title: string;
  description: string;
  discipline: string;
  type: string;
  starts_at: string;
  ends_at: string | null;
  duration_min: number | null;
  location: string;
  meeting_point: string;
  capacity: number | null;
  cancelled: boolean;
  series_id: string | null;
  organizer: { id: number | null; name: string } | null;
  organizer_id: number | null;
  updated_at: string;
  created_at: string;
  my_status: MyStatus;
  past: boolean;
  can_edit: boolean;
}
export interface EventListItem extends EventBase {
  yes: number;
  maybe: number;
  comments: number;
  preview: PersonRef[];
}
export interface EventDetail extends EventBase {
  confirmed: PersonRef[];
  waiting: PersonRef[];
  maybe: PersonRef[];
  comments: { id: number; user: PersonRef; body: string; created_at: string; can_delete: boolean }[];
}
export interface Member {
  id: number;
  first_name: string;
  last_name: string;
  photo: string | null;
}

// ---- Feedback and "Was ist neu" ----
export type FeedbackKind = 'bug' | 'idea' | 'question';
export type FeedbackStatus = 'open' | 'planned' | 'done' | 'wontfix';
export const FEEDBACK_KINDS: Record<FeedbackKind, string> = { bug: 'Fehler', idea: 'Idee', question: 'Frage' };
export const FEEDBACK_STATUS: Record<FeedbackStatus, string> = {
  open: 'Offen',
  planned: 'Geplant',
  done: 'Erledigt',
  wontfix: 'Wird nicht umgesetzt',
};
export interface Feedback {
  id: number;
  kind: FeedbackKind;
  body: string;
  page: string;
  status: FeedbackStatus;
  note: string;
  resolved_version: string | null;
  created_at: string;
  updated_at: string;
}
export interface FeedbackAdmin extends Feedback {
  author: string | null;
  user_id: number | null;
  app_version: string;
  context: { viewport?: string; device?: string };
}
export interface Release {
  version: string;
  date: string;
  title?: string;
  changes: { kind: 'neu' | 'verbessert' | 'behoben'; text: string }[];
}
export interface Changelog {
  version: string;
  seen: string | null;
  unseen: boolean;
  releases: Release[];
}

// ---- Flohmarkt ----
export type ArticleStatus = 'active' | 'reserved' | 'sold';
export interface ArticleListItem {
  id: number;
  title: string;
  description: string;
  price: number | null;
  negotiable: boolean;
  category: string;
  condition: string;
  size: string;
  status: ArticleStatus;
  seller_id: number | null;
  seller: PersonRef | null;
  thumb: string | null;
  images: number;
  questions: number;
  created_at: string;
  can_edit: boolean;
}
export interface ArticleImage {
  id: number;
  file: string;
  thumb: string;
}
export interface ArticleDetail extends Omit<ArticleListItem, 'thumb' | 'images' | 'questions'> {
  images: ArticleImage[];
  questions: {
    id: number;
    user: PersonRef;
    is_seller: boolean;
    body: string;
    created_at: string;
    can_delete: boolean;
  }[];
}

// ---- Mitglieder ----
export interface MemberCard {
  id: number;
  first_name: string;
  last_name: string;
  photo: string | null;
  role: 'member' | 'admin';
  total: number;
  disciplines: Record<string, number>;
  // admins only
  email?: string | null;
  phone?: string | null;
  city?: string | null;
  last_login_at?: string | null;
  status?: string;
  invited_at?: string | null; // last invitation (members who never signed in)
}
export interface Contact {
  email: string | null;
  phone: string | null;
  street: string | null;
  house_number: string | null;
  zip: string | null;
  city: string | null;
}
export interface MemberProfile {
  id: number;
  first_name: string;
  last_name: string;
  photo: string | null;
  role: 'member' | 'admin';
  status: 'active' | 'former' | 'disabled';
  total: number;
  disciplines: Record<string, number>;
  upcoming: {
    id: number;
    title: string;
    starts_at: string;
    ends_at: string | null;
    duration_min: number | null;
    discipline: string;
    type: string;
    location: string;
  }[];
  mates: (PersonRef & { n: number })[];
  articles: { id: number; title: string; price: number | null; status: ArticleStatus; thumb: string | null }[];
  shared: number;
  contact: Contact | null;
  last_login_at?: string | null;
  created_at?: string;
  can_edit: boolean;
}

// ---- Mitteilungen ----
export interface Post {
  id: number;
  author_id: number | null;
  author: PersonRef | null;
  title: string;
  body: string;
  pinned: boolean;
  recipients: number;
  created_at: string;
  edited_at: string | null;
  images: ArticleImage[];
  can_edit: boolean;
}
